import type {
  AhpSubmissionMetadata,
  DirectTurnTelemetryContext,
  DispatchReceipt,
  EstablishedAhpSession,
  EstablishedAhpSessionIntegrationServices,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {ClientPreparationOptions} from '@github-ui/ahp-session-client/client-interaction-observer'
import type {ClientInteractionEvent} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {Button, FormControl, Textarea} from '@primer/react'
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEventHandler,
  type ReactNode,
  type RefObject,
} from 'react'

import type {ComposerAttachmentsController} from '../hooks/use-composer-attachments'
import {
  useEstablishedAhpSessionInputController,
  type EstablishedAhpPromptDispatchKind,
} from '../hooks/use-established-ahp-session-input-controller'

export interface EstablishedAhpSessionInputIntegrations extends EstablishedAhpSessionIntegrationServices {
  readonly attachmentController?: ComposerAttachmentsController
}

export interface EstablishedAhpSessionInputProps {
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  readOnly?: boolean
  /** Keeps draft controls editable while all live-session commands remain disabled. */
  draftOnly?: boolean
  placeholder?: string
  ariaLabel?: string
  submitLabel?: string
  queueLabel?: string
  showQueue?: boolean
  showStop?: boolean
  renderActions?: boolean
  /** Custom inputs provide the draft control to focus after an accepted Stop. */
  focusTargetRef?: RefObject<HTMLElement | null>
  preparePrompt?: (text: string, options?: ClientPreparationOptions) => PreparedPrompt | Promise<PreparedPrompt>
  preparePromptForDispatch?: (
    prompt: PreparedPrompt,
    kind: EstablishedAhpPromptDispatchKind,
  ) => PreparedPrompt | Promise<PreparedPrompt>
  directTurnTelemetryContext?: DirectTurnTelemetryContext
  directTurnModelId?: string
  onInputMutation?: () => void
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>
  onSubmitAccepted?: (receipt?: DispatchReceipt) => void
  onQueueAccepted?: (receipt: DispatchReceipt) => void
  onDispatchAccepted?: (
    prompt: PreparedPrompt,
    kind: EstablishedAhpPromptDispatchKind,
    receipt?: DispatchReceipt,
  ) => void
  beforeSubmit?: () => Promise<void> | void
  beforeQueue?: () => Promise<void> | void
  beforeDispatch?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<void> | void
  /** Applies product-owned session settings before the next input mutation. */
  configureSession?: (session: EstablishedAhpSession, prompt?: PreparedPrompt) => Promise<void>
  /** See `consumePrompt` on the input controller. */
  consumePrompt?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<boolean>
  /** See `consumeSessionlessPrompt` on the input controller. */
  consumeSessionlessPrompt?: (text: string) => Promise<boolean>
  onStopped?: () => void
  onError?: (error: unknown) => void
  integrations?: EstablishedAhpSessionInputIntegrations
  /**
   * Handles the first prompt before the host has an established session.
   * Products own the coordinator and mount the returned session.
   */
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialSubmissionOrigin?: 'new' | 'recovery'
  initialSubmissionMetadata?: AhpSubmissionMetadata
  /** An initial submission started outside this component's form lifecycle. */
  initialSubmissionPending?: boolean
  /**
   * Returns a session published outside this component while prompt preparation
   * was pending. The returned session takes precedence over the initial-submission handoff.
   */
  getEstablishedSession?: () => EstablishedAhpSession | undefined
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  onInitialSubmissionStarted?: (prompt: PreparedPrompt) => void
  onInitialSubmissionSettled?: () => void
  renderInput?: (props: {
    value: string
    onValueChange: (value: string) => void
    onSubmit: (event?: ClientInteractionEvent) => void
    onQueueSubmit: (event?: ClientInteractionEvent) => void
    onStop: (event?: ClientInteractionEvent) => void
    canQueue: boolean
    canStop: boolean
    disabled: boolean
    submitDisabled: boolean
    readOnly: boolean
    placeholder: string
    ariaLabel: string
    onInputMutation?: () => void
    onPaste?: ClipboardEventHandler<HTMLTextAreaElement>
    attachments?: ComposerAttachmentsController
  }) => ReactNode
  children?: ReactNode
}

/**
 * Shared established-session input surface. Product surfaces provide picker
 * placement and service adapters, while the internal controller hook owns the
 * normalized prompt submission, queue, and stop lifecycle.
 */
export function EstablishedAhpSessionInput({
  value,
  onValueChange,
  disabled = false,
  readOnly = false,
  draftOnly = false,
  placeholder = 'Ask anything. Type @ to attach references',
  ariaLabel = 'Message',
  submitLabel = 'Send',
  queueLabel = 'Queue',
  showQueue = true,
  showStop = true,
  renderActions = true,
  focusTargetRef,
  preparePrompt,
  preparePromptForDispatch,
  directTurnTelemetryContext,
  directTurnModelId,
  onInputMutation,
  onSubmitAccepted,
  onQueueAccepted,
  onDispatchAccepted,
  beforeSubmit,
  beforeQueue,
  beforeDispatch,
  configureSession,
  consumePrompt,
  consumeSessionlessPrompt,
  onStopped,
  onError,
  integrations: integrationServices,
  initialSubmission,
  initialSubmissionOrigin = 'new',
  initialSubmissionMetadata,
  initialSubmissionPending = false,
  getEstablishedSession,
  onSessionEstablished,
  onInitialSubmissionStarted,
  onInitialSubmissionSettled,
  onPaste,
  renderInput,
  children,
}: EstablishedAhpSessionInputProps) {
  const formId = useId()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [focusRequest, setFocusRequest] = useState<{origin: Element}>()
  const settledFocusRequestRef = useRef(focusRequest)
  const {attachmentController, ...sessionIntegrationServices} = integrationServices ?? {}
  const {
    pendingAction,
    inputDisabled,
    commandsDisabled,
    isReadOnly,
    canQueue,
    canStop,
    hasActiveTurn,
    hasPendingRecoveryStop,
    handleSubmit,
    submit,
    queue,
    stop,
  } = useEstablishedAhpSessionInputController({
    value,
    disabled,
    readOnly,
    draftOnly,
    preparePrompt,
    preparePromptForDispatch,
    directTurnTelemetryContext,
    directTurnModelId,
    beforeSubmit,
    beforeQueue,
    beforeDispatch,
    configureSession,
    consumePrompt,
    consumeSessionlessPrompt,
    onSubmitAccepted,
    onQueueAccepted,
    onDispatchAccepted,
    onStopped,
    onError,
    sessionIntegrationServices,
    initialSubmission,
    initialSubmissionOrigin,
    initialSubmissionMetadata,
    initialSubmissionPending,
    getEstablishedSession,
    onSessionEstablished,
    onInitialSubmissionStarted,
    onInitialSubmissionSettled,
  })

  const stopAndRestoreFocus = useCallback(
    (event?: ClientInteractionEvent) => {
      const origin = document.activeElement
      const hadFocus = origin?.closest('form')?.id === formId
      if (stop(event) && hadFocus && origin) setFocusRequest({origin})
    },
    [formId, stop],
  )

  useLayoutEffect(() => {
    if (!focusRequest || inputDisabled || settledFocusRequestRef.current === focusRequest) return
    settledFocusRequestRef.current = focusRequest
    if (document.activeElement === document.body || document.activeElement === focusRequest.origin) {
      const target = focusTargetRef?.current ?? inputRef.current
      target?.focus()
    }
  }, [focusTargetRef, inputDisabled, focusRequest])

  return (
    <form id={formId} onSubmit={handleSubmit}>
      {renderInput ? (
        renderInput({
          value,
          onValueChange,
          onSubmit: submit,
          onQueueSubmit: queue,
          onStop: stopAndRestoreFocus,
          canQueue,
          canStop,
          disabled: inputDisabled,
          submitDisabled: commandsDisabled,
          readOnly: isReadOnly,
          placeholder,
          ariaLabel,
          onInputMutation,
          onPaste,
          attachments: attachmentController,
        })
      ) : (
        <FormControl disabled={inputDisabled}>
          <FormControl.Label>{ariaLabel}</FormControl.Label>
          <Textarea
            ref={inputRef}
            value={value}
            onChange={event => {
              onValueChange(event.target.value)
              onInputMutation?.()
            }}
            onPaste={onPaste}
            placeholder={placeholder}
            readOnly={isReadOnly}
            rows={3}
          />
        </FormControl>
      )}
      {children}
      {renderActions ? (
        <div>
          {showStop && (hasActiveTurn || hasPendingRecoveryStop) ? (
            <Button
              type="button"
              onClick={event => stopAndRestoreFocus(event.nativeEvent)}
              disabled={!canStop}
              loading={pendingAction === 'stop'}
            >
              Stop
            </Button>
          ) : null}
          {showQueue ? (
            <Button
              type="button"
              onClick={event => queue(event.nativeEvent)}
              disabled={!canQueue}
              loading={pendingAction === 'queue'}
            >
              {queueLabel}
            </Button>
          ) : null}
          <Button type="submit" disabled={commandsDisabled} loading={pendingAction === 'submit'}>
            {submitLabel}
          </Button>
        </div>
      ) : null}
    </form>
  )
}

EstablishedAhpSessionInput.displayName = 'EstablishedAhpSessionInput'
